<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="router.push({ name: 'CouponIssues', query: { status: 'used' } })">
          <i class="ri-history-line"></i>
          核銷紀錄
        </button>
        <button type="button" class="btn btn-secondary" @click="handleReset" :disabled="loading">
          <i class="ri-refresh-line"></i>
          重置
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading || !form.couponCode" @click="handleRedeem">
          <i class="ri-check-line"></i>
          {{ loading ? '核銷中...' : '確認核銷' }}
        </button>
      </div>
    </div>

    <div class="page-container">

    <div class="redeem-container">
      <div class="card">
        <div class="card-header">
          <h2 class="card-title">
            <i class="ri-coupon-line"></i>
            優惠券核銷
          </h2>
        </div>

        <div class="card-body">
          <form @submit.prevent="handleRedeem">
            <!-- 優惠券代碼 -->
            <div class="form-group">
              <label for="couponCode" class="required">
                <i class="ri-barcode-line"></i>
                優惠券代碼
              </label>
              <div class="input-group">
                <input
                  id="couponCode"
                  ref="couponCodeInput"
                  v-model="form.couponCode"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.couponCode }"
                  placeholder="請輸入或掃描優惠券代碼"
                  required
                  autofocus
                  @input="handleCouponCodeInput"
                  @paste="handleCouponCodePaste"
                  @keyup.enter="handleValidateCoupon"
                />
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="handleValidateCoupon"
                  :disabled="!form.couponCode || loading"
                >
                  <i class="ri-search-line"></i>
                  驗證
                </button>
              </div>
              <div v-if="errors.couponCode" class="invalid-feedback">
                {{ errors.couponCode }}
              </div>
              <div v-if="couponInfo" class="coupon-info">
                <div class="info-item">
                  <span class="label">優惠券名稱：</span>
                  <span class="value">{{ couponInfo.templateName }}</span>
                </div>
                <div class="info-item">
                  <span class="label">狀態：</span>
                  <span class="value" :class="getStatusClass(couponInfo.status)">
                    {{ getStatusText(couponInfo.status) }}
                  </span>
                </div>
                <div v-if="couponInfo.validUntil" class="info-item">
                  <span class="label">有效期限：</span>
                  <span class="value">{{ formatDate(couponInfo.validUntil) }}</span>
                </div>
              </div>
            </div>

            <!-- 門市選擇（選填） -->
            <div class="form-group">
              <label>
                <i class="ri-store-line"></i>
                門市
              </label>
              <StoreSelector
                v-model="form.storeId"
                :stores="stores"
                :loading="loadingStores"
              />
              <div v-if="errors.storeId" class="invalid-feedback">
                {{ errors.storeId }}
              </div>
            </div>

            <!-- 訂單 ID（選填） -->
            <div class="form-group">
              <label for="orderId">
                <i class="ri-shopping-cart-line"></i>
                訂單 ID
              </label>
              <input
                id="orderId"
                v-model="form.orderId"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.orderId }"
                placeholder="請輸入訂單 ID（選填）"
              />
              <div v-if="errors.orderId" class="invalid-feedback">
                {{ errors.orderId }}
              </div>
            </div>

            <!-- 折扣金額（選填） -->
            <div class="form-group">
              <label for="discountAmount">
                <i class="ri-money-dollar-circle-line"></i>
                實際折扣金額
              </label>
              <input
                id="discountAmount"
                v-model.number="form.discountAmount"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.discountAmount }"
                placeholder="請輸入實際折扣金額（選填）"
                min="0"
                step="0.01"
              />
              <div v-if="errors.discountAmount" class="invalid-feedback">
                {{ errors.discountAmount }}
              </div>
              <p class="form-hint">實際使用的折扣金額，用於記錄和報表</p>
            </div>

          </form>
        </div>
      </div>

      <!-- 核銷記錄 -->
      <div v-if="redeemedCoupon" class="card success-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="ri-checkbox-circle-line"></i>
            核銷成功
          </h3>
        </div>
        <div class="card-body">
          <div class="success-info">
            <div class="info-item">
              <span class="label">優惠券代碼：</span>
              <span class="value">{{ redeemedCoupon.couponCode }}</span>
            </div>
            <div class="info-item">
              <span class="label">優惠券名稱：</span>
              <span class="value">{{ redeemedCoupon.templateName || 'N/A' }}</span>
            </div>
            <div class="info-item">
              <span class="label">核銷時間：</span>
              <span class="value">{{ formatDateTime(redeemedCoupon.usedAt) }}</span>
            </div>
            <div v-if="redeemedCoupon.discountAmount" class="info-item">
              <span class="label">折扣金額：</span>
              <span class="value">NT$ {{ formatNumber(redeemedCoupon.discountAmount) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { storeService } from '@/api/modules/stores.js';
import { showSuccess, showError } from '@/utils/swal.js';
import StoreSelector from '@/components/StoreSelector.vue';

export default {
  name: 'CouponRedeem',
  components: { StoreSelector },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('優惠券核銷');

    const form = reactive({
      couponCode: '',
      orderId: '',
      storeId: '',
      discountAmount: null
    });

    const errors = reactive({
      couponCode: '',
      orderId: '',
      storeId: '',
      discountAmount: ''
    });

    const loading = ref(false);
    const loadingStores = ref(false);
    const couponInfo = ref(null);
    const stores = ref([]);
    const redeemedCoupon = ref(null);
    const couponCodeInput = ref(null);
    let validateTimer = null; // 防抖計時器

    // 載入門市列表
    const loadStores = async () => {
      try {
        loadingStores.value = true;
        const response = await storeService.getStores({
          page: 1,
          pageSize: 100,
          status: 'active'
        });

        if (response.success && response.data?.items) {
          stores.value = response.data.items;
        }
      } catch (error) {
        console.error('載入門市列表失敗:', error);
      } finally {
        loadingStores.value = false;
      }
    };

    // 驗證優惠券
    const handleValidateCoupon = async (showNotification = true) => {
      // 如果正在驗證中，不重複執行
      if (loading.value) {
        return;
      }

      if (!form.couponCode || form.couponCode.trim().length === 0) {
        errors.couponCode = '請輸入優惠券代碼';
        return;
      }

      try {
        loading.value = true;
        errors.couponCode = '';
        couponInfo.value = null;

        const response = await couponService.validateCoupon(form.couponCode.trim());

        if (response.success && response.data) {
          if (response.data.valid) {
            // 查詢優惠券詳情
            const detailResponse = await couponService.getCoupon(form.couponCode.trim());
            if (detailResponse.success && detailResponse.data) {
              couponInfo.value = detailResponse.data;
              // 只有手動點擊驗證按鈕時才顯示成功提示
              if (showNotification) {
                showSuccess('驗證成功', '優惠券可以使用');
              }
            }
          } else {
            couponInfo.value = null;
            errors.couponCode = response.data.reason || '優惠券不可用';
            // 只有手動點擊驗證按鈕時才顯示錯誤提示
            if (showNotification) {
              showError('驗證失敗', response.data.reason || '優惠券不可用');
            }
          }
        }
      } catch (error) {
        console.error('驗證優惠券失敗:', error);
        const errorMessage = error.response?.data?.message || error.message || '驗證優惠券失敗';
        errors.couponCode = errorMessage;
        couponInfo.value = null;
        // 只有手動點擊驗證按鈕時才顯示錯誤提示
        if (showNotification) {
          showError('驗證失敗', errorMessage);
        }
      } finally {
        loading.value = false;
      }
    };

    // 優惠券代碼輸入處理
    const handleCouponCodeInput = () => {
      errors.couponCode = '';
      couponInfo.value = null;
      redeemedCoupon.value = null;

      // 清除之前的計時器
      if (validateTimer) {
        clearTimeout(validateTimer);
      }

      // 如果輸入框有內容，延遲 800ms 後自動驗證（不顯示通知）
      if (form.couponCode && form.couponCode.trim().length > 0) {
        validateTimer = setTimeout(() => {
          // 確保輸入框仍有內容才驗證（避免用戶快速刪除）
          if (form.couponCode && form.couponCode.trim().length > 0) {
            handleValidateCoupon(false); // 自動驗證不顯示通知
          }
        }, 800);
      }
    };

    // 優惠券代碼貼上處理
    const handleCouponCodePaste = () => {
      // 清除之前的計時器
      if (validateTimer) {
        clearTimeout(validateTimer);
      }

      // 貼上後立即驗證（使用 nextTick 確保值已更新，不顯示通知）
      nextTick(() => {
        if (form.couponCode && form.couponCode.trim().length > 0) {
          // 稍微延遲一下確保貼上的內容已完全寫入
          setTimeout(() => {
            handleValidateCoupon(false); // 自動驗證不顯示通知
          }, 100);
        }
      });
    };

    // 表單驗證
    const validateForm = () => {
      // 清除錯誤
      Object.keys(errors).forEach(key => {
        errors[key] = '';
      });

      let isValid = true;

      // 驗證優惠券代碼
      if (!form.couponCode || form.couponCode.trim().length === 0) {
        errors.couponCode = '請輸入優惠券代碼';
        isValid = false;
      }

      // 驗證訂單 ID（如果填寫了，必須是有效的 UUID 格式）
      if (form.orderId && form.orderId.trim().length > 0) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(form.orderId.trim())) {
          errors.orderId = '訂單 ID 格式不正確';
          isValid = false;
        }
      }

      // 驗證折扣金額
      if (form.discountAmount !== null && form.discountAmount !== '') {
        const amount = parseFloat(form.discountAmount);
        if (isNaN(amount) || amount < 0) {
          errors.discountAmount = '折扣金額必須大於或等於 0';
          isValid = false;
        }
      }

      return isValid;
    };

    // 核銷優惠券
    const handleRedeem = async () => {
      if (!validateForm()) {
        return;
      }

      try {
        loading.value = true;

        const redeemData = {
          couponCode: form.couponCode.trim()
        };

        if (form.orderId && form.orderId.trim().length > 0) {
          redeemData.orderId = form.orderId.trim();
        }

        if (form.storeId && form.storeId.trim().length > 0) {
          redeemData.storeId = form.storeId.trim();
        }

        if (form.discountAmount !== null && form.discountAmount !== '') {
          redeemData.discountAmount = parseFloat(form.discountAmount);
        }

        const response = await couponService.redeemCoupon(redeemData);

        if (response.success && response.data) {
          redeemedCoupon.value = response.data;
          showSuccess('核銷成功', '優惠券已成功核銷');
          
          // 重置表單（保留優惠券代碼）
          form.orderId = '';
          form.storeId = '';
          form.discountAmount = null;
          couponInfo.value = null;
        }
      } catch (error) {
        console.error('核銷優惠券失敗:', error);
        const errorMessage = error.response?.data?.message || error.message || '核銷失敗';
        
        if (error.response?.status === 404) {
          errors.couponCode = '優惠券不存在';
        } else if (error.response?.status === 400) {
          errors.couponCode = errorMessage;
        }
        
        showError('核銷失敗', errorMessage);
      } finally {
        loading.value = false;
      }
    };

    // 重置表單
    const handleReset = () => {
      form.couponCode = '';
      form.orderId = '';
      form.storeId = '';
      form.discountAmount = null;
      couponInfo.value = null;
      redeemedCoupon.value = null;
      Object.keys(errors).forEach(key => {
        errors[key] = '';
      });

      // 清除驗證計時器
      if (validateTimer) {
        clearTimeout(validateTimer);
        validateTimer = null;
      }

      // 重置後自動聚焦到優惠券代碼輸入框
      nextTick(() => {
        setTimeout(() => {
          if (couponCodeInput.value) {
            couponCodeInput.value.focus();
          }
        }, 50);
      });
    };

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    // 格式化日期時間
    const formatDateTime = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    // 格式化數字
    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return parseFloat(num).toLocaleString('zh-TW', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    };

    // 獲取狀態文字
    const getStatusText = (status) => {
      const statusMap = {
        unused: '未使用',
        transferring: '轉送中',
        transferred_out: '已轉出',
        used: '已使用',
        expired: '已過期',
        cancelled: '已取消',
        transferred: '已轉讓'
      };
      return statusMap[status] || status;
    };

    // 獲取狀態樣式類
    const getStatusClass = (status) => {
      const classMap = {
        unused: 'status-unused',
        used: 'status-used',
        expired: 'status-expired',
        cancelled: 'status-cancelled',
        transferred: 'status-transferred',
        transferring: 'status-cancelled',
        transferred_out: 'status-transferred'
      };
      return classMap[status] || '';
    };

    // 聚焦到優惠券代碼輸入框
    const focusCouponCodeInput = () => {
      // 使用多種方法確保聚焦成功
      const focusInput = () => {
        const input = couponCodeInput.value || document.getElementById('couponCode');
        if (input) {
          input.focus();
          // 嘗試滾動到輸入框位置
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // 延遲一點再聚焦一次，確保成功
          setTimeout(() => {
            input.focus();
          }, 50);
        }
      };

      // 先等待 nextTick
      nextTick(() => {
        // 再延遲一點確保 DOM 完全渲染
        setTimeout(focusInput, 150);
      });
    };

    onMounted(async () => {
      loadStores();
      // 聚焦到優惠券代碼輸入框
      focusCouponCodeInput();
    });

    // 組件卸載時清理計時器
    onUnmounted(() => {
      if (validateTimer) {
        clearTimeout(validateTimer);
        validateTimer = null;
      }
    });

    return {
      pageTitle,
      router,
      form,
      errors,
      loading,
      loadingStores,
      couponInfo,
      stores,
      redeemedCoupon,
      couponCodeInput,
      handleValidateCoupon,
      handleCouponCodeInput,
      handleCouponCodePaste,
      handleRedeem,
      handleReset,
      formatDate,
      formatDateTime,
      formatNumber,
      getStatusText,
      getStatusClass
    };
  }
};
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.redeem-container {
  max-width: 800px;
  margin: 0 auto;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title i {
  font-size: 24px;
  color: #667eea;
}

.card-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-group label.required::after {
  content: '*';
  color: #ef4444;
  margin-left: 4px;
}

.form-group label i {
  color: #667eea;
}

.input-group {
  display: flex;
  gap: 8px;
}

.input-group .form-control {
  flex: 1;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #ef4444;
}

.form-control:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.invalid-feedback {
  display: block;
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  margin-bottom: 0;
}

.coupon-info {
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

.coupon-info .info-item {
  display: flex;
  margin-bottom: 8px;
}

.coupon-info .info-item:last-child {
  margin-bottom: 0;
}

.coupon-info .label {
  font-weight: 500;
  color: #6b7280;
  min-width: 100px;
}

.coupon-info .value {
  color: #111827;
}

.coupon-info .value.status-unused {
  color: #10b981;
  font-weight: 500;
}

.coupon-info .value.status-used {
  color: #6b7280;
}

.coupon-info .value.status-expired {
  color: #ef4444;
}

.coupon-info .value.status-cancelled {
  color: #f59e0b;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #4b5563;
}

.success-card {
  border-left: 4px solid #10b981;
}

.success-card .card-title {
  color: #10b981;
}

.success-card .card-title i {
  color: #10b981;
}

.success-info {
  padding: 12px 0;
}

.success-info .info-item {
  display: flex;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.success-info .info-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.success-info .label {
  font-weight: 500;
  color: #6b7280;
  min-width: 120px;
}

.success-info .value {
  color: #111827;
  font-weight: 500;
}

</style>
